import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { Observable, of, forkJoin } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import {
  ModalService,
  BannerData,
  SectionCardSaudeData,
  CardData,
  TaxData,
  Step,
} from '@fcp/lib-ui';
import * as pluginDataLabels from 'chartjs-plugin-datalabels';

import { HttpClientService } from '../../http/http-client.service';
import { CMSSessionService } from '../session/cms-session.service';
import { OverlayService } from '../../../shared/overlay/overlay.service';
import { IPService } from '../../../essentials/ip/ip.service';

import { CookieService } from 'ngx-cookie-service';

// tslint:disable-next-line: nx-enforce-module-boundaries
import { environment } from 'apps/portal/src/environments/environment';
import {
  ModalQuery,
  ServicesQuery,
  ProfilesQuery,
  InsuranceQuery,
  BenefitsQuery,
  PagesQuery,
  FilterQuery,
  NavActionsQuery,
  ConfigNewsQuery,
  HighlightNewsQuery,
  HealthPlansQuery,
  HighlightPublishingQuery,
  NewHighlightQuery,
  PublishingHighlightQuery,
  MenuHamburgerQuery,
  FooterLinksQuery,
  searchPageByTags,
  searchNewsQuery,
  searchPublicationsQuery,
  HelpdeskQuery,
  publishingAboutQuery,
  SurveyQuery,
  FooterQuery,
  ConfigQuery,
  PagesSlugQuery,
  NewsSlugQuery,
  PublishingSlugQuery,
  FindNewsByIdQuery,
} from '../graphql/query.model';

import { ArticleData } from '../../../detail-highlight/detail-highlight.component';
import { ApplicationInsightsService } from './../../application-insights/application-insights.service';

export interface AuthToken {
  access_token: string;
  expires_in: number;
  token_type: string;
}

export interface PlanDataResponse {
  data: {
    planos: { iv: Array<any> };
    titulo: { iv: string };
  };
}

@Injectable({
  providedIn: 'root',
})
export class CMSContentService extends HttpClientService {
  public host =
    environment.configCMS.endPointAPI + environment.configCMS.endPointAPP;
  public serviceImg: boolean;

  private assetsURL = environment.configCMS.endPointAssets;

  constructor(
    private router: Router,
    public http: HttpClient,
    private cmsSessionService: CMSSessionService,
    private cookieService: CookieService,
    private modalService: ModalService,
    private overlay: OverlayService,
    private IP: IPService,
    private applicationInsightsService: ApplicationInsightsService
  ) {
    super(http);
  }

  public return(schema: string, params?: any): Observable<any> {
    const url = this.host + schema;
    const token = this.cmsSessionService.getToken();

    return this.get(url, params, '', token);
  }

  public preview(schema: string, params?: any): Observable<any> {
    const url = this.host + schema;
    const token = this.cmsSessionService.getToken();

    return this.post(url, params, 'preview', token);
  }

  public send(schema: string, params?: any): Observable<any> {
    const url = this.host + schema;
    const token = this.cmsSessionService.getToken();

    return this.post(url, params, '', token);
  }

  public getFilter(name: string) {
    const schema = 'graphql';
    const params = {
      query: FilterQuery(name),
    };

    const url = this.host + schema;
    const token = this.cmsSessionService.getToken();

    return this.post(url, params, '', token).pipe(
      map((response: any) =>
        response.body && response.body.data
          ? response.body.data.queryFiltrosContents
          : []
      ),
      map((response: any) => {
        return response.map((item) => {
          return this.buildResponseFilterObject(item.data);
        });
      })
    );
  }

  public getConfigNews() {
    const schema = 'graphql';
    const params = {
      query: ConfigNewsQuery(),
    };

    const url = this.host + schema;
    const token = this.cmsSessionService.getToken();

    return this.post(url, params, '', token).pipe(
      map((value: HttpResponse<any>) => {
        return value.body.data.queryConfiguracaoNoticiaContents.map(
          (response) => {
            const configs = response.data;
            return {
              banner:
                configs.banner && configs.banner.iv && configs.banner.iv[0]
                  ? configs.banner.iv[0].id
                  : '',
              blocks: {
                bigger:
                  configs.destaqueGrande &&
                  configs.destaqueGrande.iv &&
                  configs.destaqueGrande.iv[0]
                    ? configs.destaqueGrande.iv[0].id
                    : '',
                firstSmall:
                  configs.destaquePequeno1 &&
                  configs.destaquePequeno1.iv &&
                  configs.destaquePequeno1.iv[0]
                    ? configs.destaquePequeno1.iv[0].id
                    : '',
                secoundSmall:
                  configs.destaquePequeno2 &&
                  configs.destaquePequeno2.iv &&
                  configs.destaquePequeno2.iv[0]
                    ? configs.destaquePequeno2.iv[0].id
                    : '',
              },
            };
          }
        );
      })
    );
  }

  public getHighlightNews(top) {
    const schema = 'graphql';
    const params = {
      query: HighlightNewsQuery(top, 'data/Data/iv desc'),
    };

    const url = this.host + schema;
    const token = this.cmsSessionService.getToken();

    return this.post(url, params, '', token).pipe(
      map((value: HttpResponse<any>) => {
        return value.body.data.queryNoticiasContents.map((response) => {
          const configs = response.data;
          return {
            id: response.id,
            title: configs.titulo ? configs.titulo.iv : '',
            description: configs.subtitulo ? configs.subtitulo.iv : '',
            banner:
              configs.imagemDestaque &&
              configs.imagemDestaque.iv &&
              configs.imagemDestaque.iv[0]
                ? this.assetsURL + configs.imagemDestaque.iv[0].id
                : '',
            date: configs.data ? configs.data.iv : '',
            slug: configs.slug ? configs.slug.iv : '',
            category: configs.categoria ? configs.categoria.iv : '',
          } as {
            date: string;
            category: string;
            banner: string;
            slug: string;
            description: string;
            title: string;
            id: string;
          };
        });
      }),
      catchError((error) => {
        if (!environment.production) {
          console.log(error);
        }
        this.router.navigate(['/nao-encontrado']);
        return of(null);
      })
    );
  }

  public getHighlightPublishing() {
    const schema = 'graphql';
    const params = {
      query: HighlightPublishingQuery(),
    };

    const url = this.host + schema;
    const token = this.cmsSessionService.getToken();

    return this.post(url, params, '', token).pipe(
      map((value: HttpResponse<any>) => {
        return value.body.data.queryPublicacoesContents.map(
          (response: any): ArticleData => {
            const configs = response.data;

            let documents = [];
            if (configs.documents) {
              documents = configs.documents.iv
                ? configs.documents.iv.map((document) => {
                    return {
                      title: document.title || '',
                      pdf: document.pdf[0] ? document.pdf[0].url : '',
                    };
                  })
                : [];
            }

            return {
              id: response.id,
              title: configs.titulo ? configs.titulo.iv : '',
              description: configs.subtitulo ? configs.subtitulo.iv : '',
              banner:
                configs.imagemDestaque &&
                configs.imagemDestaque.iv &&
                configs.imagemDestaque.iv[0]
                  ? configs.imagemDestaque.iv[0].url
                  : '',
              date: configs.data ? configs.data.iv : '',
              slug: configs.slug ? configs.slug.iv : '',
              documents,
              category: configs.category ? configs.category.iv : '',
              subcategory: configs.subcategory ? configs.subcategory.iv : '',
              content: configs.conteudo ? configs.conteudo.iv : '',
            };
          }
        );
      }),
      catchError((error) => {
        if (!environment.production) {
          console.log(error);
        }
        this.router.navigate(['/nao-encontrado']);
        return of(null);
      })
    );
  }

  public getNewHighlight(slug) {
    const schema = 'graphql';
    const params = {
      query: NewHighlightQuery(slug),
    };

    const url = this.host + schema;
    const token = this.cmsSessionService.getToken();

    return this.post(url, params, '', token).pipe(
      map((value: HttpResponse<any>) => {
        return value.body.data.queryNoticiasContents.map((response) => {
          const configs = response.data;

          let documents = [];
          if (configs.documents) {
            documents = configs.documents.iv
              ? configs.documents.iv.map((document) => {
                  return {
                    title: document.title || '',
                    pdf: document.pdf[0] ? document.pdf[0].url : '',
                  };
                })
              : [];
          }

          return {
            id: response.id,
            title: configs.titulo ? configs.titulo.iv : '',
            description: configs.subtitulo ? configs.subtitulo.iv : '',
            banner:
              configs.imagemDestaque &&
              configs.imagemDestaque.iv &&
              configs.imagemDestaque.iv[0]
                ? this.assetsURL + configs.imagemDestaque.iv[0].id
                : '',
            date: configs.data ? configs.data.iv : '',
            slug: configs.slug ? configs.slug.iv : '',
            content: configs.conteudo ? configs.conteudo.iv : '',
            documents,
            tags: configs.tags ? configs.tags.iv : [],
            category: configs.categoria ? configs.categoria.iv : '',
            subcategory: configs.subcategory ? configs.subcategory.iv : '',
          } as ArticleData;
        });
      }),
      catchError((error) => {
        if (!environment.production) {
          console.log(error);
        }
        this.router.navigate(['/nao-encontrado']);
        return of(null);
      })
    );
  }

  public getPublishingHighlight(slug) {
    const schema = 'graphql';
    const params = {
      query: PublishingHighlightQuery(slug),
    };

    const url = this.host + schema;
    const token = this.cmsSessionService.getToken();

    return this.post(url, params, '', token).pipe(
      map((value: HttpResponse<any>) => {
        return value.body.data.queryPublicacoesContents.map((response) => {
          const configs = response.data;

          let documents = [];
          if (configs.documents) {
            documents = configs.documents.iv
              ? configs.documents.iv.map((document) => {
                  return {
                    title: document.title || '',
                    pdf: document.pdf[0] ? document.pdf[0].url : '',
                  };
                })
              : [];
          }

          return {
            id: response.id,
            title: configs.titulo ? configs.titulo.iv : '',
            description: configs.subtitulo ? configs.subtitulo.iv : '',
            banner:
              configs.imagemDestaque &&
              configs.imagemDestaque.iv &&
              configs.imagemDestaque.iv[0]
                ? configs.imagemDestaque.iv[0].url
                : '',
            date: configs.data ? configs.data.iv : '',
            slug: configs.slug ? configs.slug.iv : '',
            documents,
            video:
              configs.video &&
              configs.video.iv &&
              configs.video.iv[0] &&
              configs.video.iv[0].data &&
              configs.video.iv[0].data.titulo &&
              configs.video.iv[0].data.urlIframe
                ? {
                    title: configs.video.iv[0].data.titulo.iv,
                    urlIframe: configs.video.iv[0].data.urlIframe.iv,
                  }
                : null,
            content: configs.conteudo ? configs.conteudo.iv : '',
            category: configs.category ? configs.category.iv : '',
            subcategory: configs.subcategory ? configs.subcategory.iv : '',
          } as ArticleData;
        });
      }),
      catchError((error) => {
        if (!environment.production) {
          console.log(error);
        }
        this.router.navigate(['/nao-encontrado']);
        return of(null);
      })
    );
  }

  public getModal(modal: string, preview?: boolean) {
    const schema = 'graphql';
    const params = {
      query: ModalQuery(modal),
    };

    const url = this.host + schema;
    const token = this.cmsSessionService.getToken();

    if (preview) {
      return this.post(url, params, 'preview', token).pipe(
        map((value: HttpResponse<any>) => {
          return value.body.data.queryModalContents.map((response) => {
            if (response.flatData) {
              const obj = response.flatData;
              const tabs = this.buildTabsModal(obj);
              const buttons = this.buildButtonsModal(obj);
              let data = [];
              if (obj.tipoConteudo) {
                switch (obj.tipoConteudo) {
                  case 'Modal Cards':
                    data = this.buildSectionFlowDataModal(obj);
                    break;
                  default:
                    data = this.buildAccordionModal(obj);
                    break;
                }
              }
              const components = this.buildComponentsModal(obj);
              const documents = this.buildDocumentsModal(obj);

              return {
                tabs,
                buttons,
                data,
                components,
                documents,
                img:
                  obj.imagem && obj.imagem[0]
                    ? this.assetsURL + obj.imagem[0].id
                    : '',
                title: obj.titulo,
                titleTabs: obj.tituloAbas ? obj.tituloAbas : '',
                subtitleTabs: obj.subtituloAbas ? obj.subtituloAbas : '',
                type: obj.tipoConteudo ? obj.tipoConteudo : '',
                text: obj.texto ? obj.texto : '',
              };
            }
            return null;
          });
        })
      );
    } else {
      return this.post(url, params, '', token).pipe(
        map((value: HttpResponse<any>) => {
          return value.body.data.queryModalContents.map((response) => {
            if (response.flatData) {
              const obj = response.flatData;
              const tabs = this.buildTabsModal(obj);
              const buttons = this.buildButtonsModal(obj);
              let data = [];
              if (obj.tipoConteudo) {
                switch (obj.tipoConteudo) {
                  case 'Modal Cards':
                    data = this.buildSectionFlowDataModal(obj);
                    break;
                  default:
                    data = this.buildAccordionModal(obj);
                    break;
                }
              }
              const components = this.buildComponentsModal(obj);
              const documents = this.buildDocumentsModal(obj);

              return {
                tabs,
                buttons,
                data,
                components,
                documents,
                img:
                  obj.imagem && obj.imagem[0]
                    ? this.assetsURL + obj.imagem[0].id
                    : '',
                title: obj.titulo,
                titleTabs: obj.tituloAbas ? obj.tituloAbas : '',
                subtitleTabs: obj.subtituloAbas ? obj.subtituloAbas : '',
                type: obj.tipoConteudo ? obj.tipoConteudo : '',
                text: obj.texto ? obj.texto : '',
              };
            }
            return null;
          });
        })
      );
    }
  }

  public getProfiles() {
    const params = {
      query: ProfilesQuery(),
    };
    return this.send('graphql', params).pipe(
      map((profile) => {
        const items = profile.body.data.queryHomePerfisContents;
        return items.map((item) => {
          const data = item.data;
          const services = data['servicos'].iv.map((service) => {
            const modal =
              service.modal && service.modal[0]
                ? service.modal && service.modal[0]
                : false;
            const externalURL = service.url.indexOf('http') >= 0;
            return {
              title: service.titulo,
              url: externalURL ? '/' + service.url : null,
              router: externalURL ? null : '/' + service.url,
              hrefTarget: externalURL ? '_blank' : null,
              modal: modal ? modal.id : '',
              isCredential:
                modal &&
                modal.data &&
                modal.data.tipoConteudo &&
                modal.data.tipoConteudo.iv === 'Rede Credenciada',
            };
          });

          const typename = data['linkBotaoPagina']
            ? data['linkBotaoPagina'].iv[0].__typename
            : null;
          let pageBase = null;
          let pageRoute = null;
          if (data['linkBotaoPagina']) {
            const pageData = data['linkBotaoPagina'].iv[0].data;
            if (typename) {
              if (typename === 'Filtros') {
                pageBase = '/filtro/';
              } else {
                pageBase = '/paginas/';
              }
              const pageSlug = pageData.nome
                ? pageData.nome.iv
                : pageData.slug.iv;
              pageRoute = pageBase + pageSlug;
            }
          }

          return {
            title: data['titulo'].iv,
            button: [
              {
                title: data['descricaoBotao'].iv,
                url: data['linkBotaoUrl'] ? data['linkBotaoUrl'].iv : null,
                openUrl: data['abrirURL'] ? data['abrirURL'].iv : null,
                page: pageRoute,
                color: 'Básico',
              },
            ],
            icon: this.assetsURL + data['icone'].iv[0].id,
            page: pageRoute,
            url: data['linkBotaoUrl'] ? data['linkBotaoUrl'].iv : null,
            modal: data['modal'] ? data['modal'].iv[0].id : null,
            services,
            linkPageMoreServices: data['linkPageMaisServicos']
              ? `/paginas/${data['linkPageMaisServicos'].iv[0].data.slug.iv}`
              : null,
          };
        });
      })
    );
  }

  public getInsurance(nome: string): Observable<any> {
    const schema = 'graphql';
    const params = {
      query: InsuranceQuery(),
    };

    return this.send(schema, params).pipe(
      map((response) => {
        const items = response.body.data.queryBeneficiosContents[0].data;
        return this.mapInsurance(items, nome);
      })
    );
  }

  public getBenefits(nome: string): Observable<any> {
    const schema = 'graphql';
    const params = {
      query: BenefitsQuery(nome),
    };

    return this.send('graphql', params).pipe(
      map((response) => {
        const items = response.body.data.queryBeneficiosContents[0].data;
        return this.mapBenefits(items);
      })
    );
  }

  public getService(nome: string): Observable<any> {
    const params = {
      query: ServicesQuery(`data/Categoria/iv eq '${nome}'`),
    };

    return this.send('graphql', params).pipe(
      map((res) => {
        return this.mapServices(res.body.data.queryServicosContents[0]?.data);
      })
    );
  }

  public getPagesSlug(id: string): Observable<any> {
    const requests = [
      this.send('graphql', { query: PagesSlugQuery(id) }),
      this.send('graphql', { query: NewsSlugQuery(id) }),
      this.send('graphql', { query: PublishingSlugQuery(id) }),
    ];

    return forkJoin(requests);
  }

  public getPage(slug: string): Observable<any> {
    const params = {
      query: PagesQuery(slug),
    };

    return this.send('graphql', params).pipe(
      map((response) => {
        const items =
          response.body.data.queryPaginasContents[0].data.componentes.iv;
        return this.mapFilterGraphQL(items);
      }),
      catchError((error) => {
        if (!environment.production) {
          console.log(error);
        }
        this.applicationInsightsService.logPageView('nao-encontrado', slug);
        this.router.navigate(['/nao-encontrado']);
        return of(null);
      })
    );
  }

  public getPreviewPage(slug: string): Observable<any> {
    const params = {
      query: PagesQuery(slug),
    };

    return this.preview('graphql', params).pipe(
      map((response) => {
        const items =
          response.body.data.queryPaginasContents[0].data.componentes.iv;
        return this.mapFilterGraphQL(items);
      }),
      catchError((error) => {
        if (!environment.production) {
          console.log(error);
        }
        this.applicationInsightsService.logPageView('nao-encontrado', slug);
        this.router.navigate(['/nao-encontrado']);
        return of(null);
      })
    );
  }

  public getNavActions(): Observable<any> {
    const params = {
      query: NavActionsQuery(),
    };

    return this.send('graphql', params).pipe(
      map((response) => {
        const items = response.body.data.queryNavActionsContents.splice(0, 1);
        return this.mapNavActions(items);
      }),
      catchError((error) => {
        if (!environment.production) {
          console.log(error);
        }
        return of(null);
      })
    );
  }

  public getFooterLinks(): Observable<any> {
    const params = {
      query: FooterLinksQuery(),
    };

    return this.send('graphql', params).pipe(
      map((response) => {
        const listData = response.body.data.queryRodapeLinksContents;
        return listData
          .map((item) => {
            const data = item.data;
            let links = [];
            if (data.links) {
              links = data.links.iv.map((linkItem) => {
                let modal = null;
                let page = '';
                if (linkItem.modal && linkItem.modal[0]) {
                  modal = linkItem.modal
                    .reduce((acc, modalItem) => {
                      acc.push(modalItem.id);
                      return acc;
                    }, [])
                    .toString();
                }
                if (
                  linkItem.page &&
                  linkItem.page[0] &&
                  linkItem.page[0].data &&
                  linkItem.page[0].data.slug
                ) {
                  page = '/paginas/' + linkItem.page[0].data.slug.iv;
                }
                linkItem.modal = modal;
                linkItem.page = page;
                return linkItem;
              });
            }

            let buttons = [];
            if (data.buttons) {
              buttons = data.buttons.iv.map((button) => {
                button.page =
                  button.page &&
                  button.page[0] &&
                  button.page[0].data &&
                  button.page[0].data.slug
                    ? `/paginas/${button.page[0].data.slug.iv}`
                    : '/';
                return button;
              });
            }
            return {
              order: data.ordem.iv,
              title: data.titulo.iv,
              buttons,
              links,
              marker: data.estiloMarcador ? data.estiloMarcador.iv : [],
              description: data.descricao ? data.descricao.iv : '',
            };
          })
          .sort((a, b) => a.order - b.order);
      }),
      catchError((error) => {
        if (!environment.production) {
          console.log(error);
        }
        return of(null);
      })
    );
  }

  public getNewsById(id: string): Observable<any> {
    const params = {
      query: FindNewsByIdQuery(id),
    };

    return this.send('graphql', params).pipe(
      map((response) => {
        const items = response.body.data.queryNoticiasContents;
        return items.pop();
      }),
      map((response) => response.flatData),
      catchError((error) => {
        if (!environment.production) {
          console.log(error);
        }
        return of(null);
      })
    );
  }

  public getMenuHamburger(): Observable<any> {
    const params = {
      query: MenuHamburgerQuery(),
    };

    return this.send('graphql', params).pipe(
      map((response) => {
        const items = response.body.data.queryMenuHamburgerContents;
        return this.mapMenuHamburger(items);
      }),
      catchError((error) => {
        if (!environment.production) {
          console.log(error);
        }
        return of(null);
      })
    );
  }

  public mapFilterGraphQL(components) {
    const hasItems = components.length > 0;
    const isObject = components.length === 1;
    const isArray = components.length > 1;

    if (hasItems) {
      if (isArray) {
        return components.map((component) => {
          return this.buildResponseGraphQLObject(component);
        });
      }

      if (isObject) {
        return [this.buildResponseGraphQLObject(components[0])];
      }
    }
  }

  private buildDocumentsModal(obj: any) {
    let documents = [];
    if (obj.documentos) {
      documents = obj.documentos.map((documento) => {
        return {
          title: documento.titulo ? documento.titulo : '',
          url:
            documento.pDF && documento.pDF[0]
              ? this.assetsURL + documento.pDF[0].id
              : '',
        };
      });
    }
    return documents;
  }

  private buildResponseGraphQLObject(component) {
    const newComponent = component;
    switch (newComponent.__typename) {
      case 'Banners':
        if (newComponent && component) {
          newComponent.data = this.mapBanner(component.flatData);
        }
        break;
      case 'Formulario':
        newComponent.data = this.buildReponseFormularioObject(component.data);
        break;
      case 'Beneficios':
        newComponent.data = this.mapBenefits(component.data);
        break;
      case 'Certificacoes':
        newComponent.data = this.mapCertificationsData(component.data);
        break;
      case 'PaginasIncorporadas':
        newComponent.data = this.mapPageInc(component.flatData);
        break;
      case 'Social':
        newComponent.data = this.buildReponseSocialObject(component.data);
        break;
      case 'Taxas':
        newComponent.data = this.buildResponseTaxsObject(component.data);
        break;
      case 'Secao':
        newComponent.data = this.mapSection(component.data);
        break;
      case 'Servicos':
        newComponent.data = this.mapServices(component.data);
        break;
      case 'ServicosGrupo':
        newComponent.data = {
          title: component.data.title.iv,
          subtitle: component.data.subtitle.iv,
          list: component.data.listaServicos.iv.map((serv) => {
            return this.mapServices(serv.data);
          }),
        };
        break;
      case 'Vantagens':
        newComponent.data = this.buildFeatureItensArray(component.data);
        break;
      case 'Contadores':
        newComponent.data = this.buildResponseCounterObject(component.data);
        break;
      case 'DestaquePlanosSaude':
        newComponent.data = this.buildReponseHighlightHealthObject(
          component.data
        );
        break;
      case 'Informativo':
        newComponent.data = this.buildResponseCardInfoObject(
          component.flatData
        );
        break;
      case 'CardIconeTituloLink':
        newComponent.data = this.buildResponseCardIconeTituloLinkObject(
          component.flatData
        );
        break;
      case 'IconeTitulo':
        newComponent.data = this.buildResponseIconeTituloObject(
          component.flatData
        );
        break;
      case 'DescricaoCorFundo':
        newComponent.data = this.buildResponseDescricaoCorFundoObject(
          component.flatData
        );
        break;
      case 'ImagemTituloDescricao':
        newComponent.data = this.buildResponseImagemTituloDescricaoObject(
          component.flatData
        );
        break;
      case 'Imagem':
        newComponent.data = this.buildResponseImagemObject(
          component.flatData
        );
        break;
      case 'LinkInternoVoltar':
        newComponent.data = this.buildResponseLinkInternoVoltarObject(
          component.flatData
        );
        break;
      case 'TituloSubtituloCorFundo':
        newComponent.data = this.buildResponseTituloSubtituloCorFundoObject(
          component.flatData
        );
        break;
      case 'UnidadesClinica':
        newComponent.data = this.mapClinicas(component.flatData);
        break;
      case 'BannerTextoGrande':
        newComponent.data = this.buildResponseBannerTextoGrandeObject(
          component.flatData
        );
        break;
      case 'DestaqueNavegacao':
        newComponent.data = this.buildResponseHighlightAnchorObject(
          component.flatData
        );
        break;
      case 'NoticiasCarrossel':
        newComponent.data = this.buildResponseNewsCarousel(component.flatData);
        break;
      case 'Graficos':
        newComponent.data = this.buildReponseChartsObject(component.data);
        break;
      case 'Faq':
        newComponent.data = this.mapFaq(component.flatData);
        break;
      default:
        break;
    }
    return newComponent;
  }

  private mapCertificationsData(data: any): any {
    return {
      img:
        data.img && data.img.iv && data.img.iv[0]
          ? this.assetsURL + data.img.iv[0].id
          : '',
      subtitle: data.subtitle ? data.subtitle.iv : '',
      title: data.title ? data.title.iv : '',
    };
  }

  private buildResponseTaxsObject(data: any) {
    let links = [];
    if (data.links && data.links.iv) {
      links = data.links.iv.map((link) => {
        link.icon =
          link.icon && link.icon[0] ? this.assetsURL + link.icon[0].id : '';
        return link;
      });
    }
    return {
      title: data.title ? data.title.iv : '',
      description: data.description ? data.description.iv : '',
      links,
      icon:
        data.icon && data.icon.iv && data.icon.iv[0]
          ? this.assetsURL + data.icon.iv[0].id
          : '',
    } as TaxData;
  }

  private buildResponseFilterObject(response) {
    const buttons = response.botoes.iv.map((button) => {
      let externalURL = false;
      if (button.uRL) {
        if (button.uRL.indexOf('http') >= 0) {
          externalURL = true;
        }
      }

      return {
        title: button.titulo,
        url: button.pagina ? null : button.uRL,
        router: externalURL ? null : button.uRL ? button.uRL : null,
        hrefTarget: externalURL ? '_blank' : null,
        page: button.pagina
          ? '/paginas/' + button.pagina[0].data.slug.iv
          : null,
        color: button.cor,
      };
    });
    const cards = response.cards.iv.map((card) => {
      return {
        title: card.titulo,
        subtitle: card.descricao,
        link:
          card.navegarPara &&
          card.navegarPara[0] &&
          card.navegarPara[0].data &&
          card.navegarPara[0].data.slug &&
          card.navegarPara[0].data.slug.iv
            ? '/paginas/' + card.navegarPara[0].data.slug.iv
            : '',
      };
    });
    const hasLinks =
      response.linksHeader !== null && response.linksHeader !== undefined;
    let linksHeader = [];
    if (hasLinks) {
      linksHeader = response.linksHeader.iv.map((link) => {
        return {
          title: link.titulo,
          icon:
            link.icone && link.icone[0]
              ? this.assetsURL + link.icone[0].id
              : '',
          url: link.pagina
            ? '/paginas/' + link.pagina[0].data.slug.iv
            : link.uRL,
        };
      });
    }
    const hasDescription =
      response.descricaoDestaque !== null &&
      response.descricaoDestaque !== undefined;

    return {
      buttons,
      cards,
      descriptionHighlight: hasDescription ? response.descricaoDestaque.iv : '',
      linksHeader,
      subtitle: response.subtitulo.iv,
      title: response.titulo.iv,
    };
  }

  private buildReponseServiceObject(response) {
    let buttons = [];
    if (response.Botoes) {
      buttons = response.Botoes.iv.map((button) => {
        return {
          title: button['Titulo'],
          url: button['URL'],
          color: button['Cor'],
          modal:
            button['Modal'] && button['Modal'][0] ? button['Modal'][0].id : '',
          page:
            button.Pagina && button.Pagina.length > 0
              ? '/paginas/' + button.Pagina[0]
              : '',
        };
      });
    }
    const icons = response['Icones'].iv.map((icon) => {
      return {
        title: icon['Titulo'],
        url: icon['URL'],
        image: this.assetsURL + icon['Imagem'],
      };
    });
    let tips;
    if (response['Dicas'] !== null && response['Dicas'] !== undefined) {
      tips = response['Dicas'].iv.map((tip) => {
        return {
          description: tip['Descricao'],
        };
      });
    } else {
      tips = response['Dicas'];
    }
    const hasHighlightColor =
      response['CorDestaque'] !== null && response['CorDestaque'] !== undefined;
    const hasDescritption =
      response['Descricao'] !== null && response['Descricao'] !== undefined;
    return {
      buttons,
      categories: response['Categoria'].iv,
      highlightColor: hasHighlightColor
        ? response['CorDestaque'].iv
        : response['CorDestaque'],
      tips,
      icons,
      image: this.assetsURL + response['Imagem'].iv,
      subtitle: response['Subtitulo'].iv,
      description: hasDescritption ? response['Descricao'].iv : '',
      title: response['Titulo'].iv,
    };
  }

  private buildComponentsModal(obj: any) {
    let components = [];
    if (obj['componentes']) {
      components = obj['componentes'].map((item) => {
        let data;
        const newCard = item.flatData;
        switch (item.__typename) {
          case 'ModalCards':
            if (newCard.cards) {
              data = newCard.cards.map((card) => {
                return {
                  title: card.titulo ? card.titulo : '',
                  subtitle: card.descricao ? card.descricao : '',
                  icon:
                    card['icone'] && card['icone'][0]
                      ? this.assetsURL + card['icone'][0].id
                      : card['icone'],
                };
              });
            }
            break;
          case 'ModalDestaque':
            if (newCard.cards) {
              data = newCard.cards.map((card) => {
                return {
                  title: card.titulo ? card.titulo : '',
                  subtitle: card.descricao ? card.descricao : '',
                  icon:
                    card['icone'] && card['icone'][0]
                      ? this.assetsURL + card['icone'][0].id
                      : card['icone'],
                };
              });
            }
            break;
          case 'ModalLinhaTempo':
            data = newCard.eventos.map((event) => {
              return {
                title: event.titulo ? event.titulo : '',
                description: event.descricao ? event.descricao : '',
                year: event.ano ? event.ano : '',
              };
            });
            break;
          case 'ModalPerfil':
            data = newCard.itens.map((itemValue) => {
              return {
                title: itemValue.titulo ? itemValue.titulo : '',
                description: itemValue.descricao ? itemValue.descricao : '',
                color: itemValue.cor ? itemValue.cor : '',
                img:
                  itemValue.imagem && itemValue.imagem[0]
                    ? this.assetsURL + itemValue.imagem[0].id
                    : itemValue.imagemFundo && itemValue.imagemFundo[0]
                    ? this.assetsURL + itemValue.imagemFundo[0].id
                    : '',
                footer: itemValue.rodape ? itemValue.rodape : '',
                cols: itemValue.colunas,
                rows: itemValue.linhas,
                descriptionActionButton: itemValue.botaoDescricao,
                icon:
                  itemValue.icone && itemValue.icone[0]
                    ? this.assetsURL + itemValue.icone[0].id
                    : '',
                modal: itemValue.modal,
                subtitle: itemValue.subtitulo,
              };
            });
            break;
          case 'Beneficios':
            data = this.mapBenefitsFlat(newCard);
            break;
          case 'PaginasIncorporadas':
            data = this.mapPageInc(newCard);
            break;
          case 'TabelasDinamicas':
            data = this.mapDynamicTable(newCard);
            break;
          default:
            data = item.flatData;
            break;
        }

        const responseObject = {
          __typename: item.__typename,
          title: newCard.titulo ? newCard.titulo : '',
          subtitle: newCard.subtitulo ? newCard.subtitulo : '',
          description: newCard.descricao ? newCard.descricao : '',
          type: newCard.tipo ? newCard.tipo : '',
          data,
        };

        return responseObject;
      });
    }
    return components;
  }

  private buildSectionFlowDataModal(obj: any) {
    let data = [];
    if (obj['conteudo']) {
      data = obj['conteudo'].map((item) => {
        return {
          title: item['titulo'],
          subtitle: item['descricao'],
          img:
            item['imagem'] && item['imagem'][0]
              ? this.assetsURL + item['imagem'][0].id
              : item['imagem'],
        };
      });
    }
    return data;
  }

  private buildAccordionModal(obj: any) {
    let data = [];
    if (obj.conteudo) {
      data = obj.conteudo.map((item) => {
        let externalLinks = [];
        if (item.externalLinks && item.externalLinks.length > 0) {
          externalLinks = item.externalLinks.map((link) => {
            return {
              title: link.flatData.title,
              url: link.flatData.url,
            };
          });
        }
        let documents = [];
        if (item.documentos && item.documentos.length > 0) {
          documents = item.documentos[0].flatData.documents.map((response) => {
            return {
              title: response.title,
              link: this.assetsURL + response.file[0].id,
            };
          });
        }
        // let accordion = [];
        // if (item.accordionExtra && item.accordionExtra.length > 0) {
        //   accordion = item.accordionExtra[0].flatData.questions.map(
        //     response => {
        //       return {
        //         title: response.title,
        //         description: response.description
        //       };
        //     }
        //   );
        // }
        const acc = { componentes: [] };
        acc.componentes = item.components;
        const components = this.buildComponentsModal(acc);
        return {
          title: item.titulo,
          description: item.descricao,
          img:
            item['imagem'] && item['imagem'][0]
              ? this.assetsURL + item['imagem'][0].id
              : item['imagem'],
          externalLinks,
          documents,
          components,
        };
      });
    }
    return data;
  }

  private buildButtonsModal(obj: any) {
    let buttons = [];
    if (obj.botoes) {
      buttons = obj.botoes.map((button) => {
        return {
          title: button.titulo,
          url: button.uRL === null ? '' : button.uRL,
          color: button.cores,
        };
      });
    }
    return buttons;
  }

  private buildTabsModal(obj: any) {
    let tabs = [];
    if (obj.componentes) {
      tabs = obj.componentes.map((tab) => {
        const newTab = tab.flatData;
        let components = [];

        if (newTab.components) {
          components = newTab.components.map((component) => {
            let accordions = [];
            const tabComponent = component.flatData;
            if (tabComponent.conteudo) {
              accordions = tabComponent.conteudo.map((accordion) => {
                return {
                  title: accordion.titulo,
                  description: accordion.descricao,
                };
              });
            }
            return {
              __typename: tabComponent.__typename,
              accordions,
            };
          });
        }
        const cards = this.buildCardsModal(newTab);
        const sections = this.buildSectionModal(newTab);
        return {
          id: tab.id || null,
          title: newTab.titulo ? newTab.titulo : '',
          group: newTab.grupo ? newTab.grupo : '',
          color: newTab.corDeFundo ? newTab.corDeFundo : '',
          category: newTab.categoria ? newTab.categoria : '',
          description: newTab.descricao ? newTab.descricao : '',
          components,
          cards,
          sections,
        };
      });
    }
    return tabs;
  }

  private buildSectionModal(newTab: any) {
    let sections = [];
    if (newTab.seccoes) {
      sections = newTab.seccoes.map((seccao) => {
        const data = seccao.flatData;
        const cards = this.buildSectionCardsModal(data.cards);
        return {
          title: data.titulo ? data.titulo : '',
          subtitle: data.subtitulo ? data.subtitulo : '',
          cards,
        };
      });
    }
    return sections;
  }

  private buildSectionCardsModal(cardsOld: any) {
    let cards = [];
    if (cardsOld) {
      cards = cardsOld.map((card) => {
        return {
          title: card.titulo,
          description: card.descricao,
        };
      });
    }
    return cards;
  }

  private buildCardsModal(newTab: any) {
    let cards = [];
    if (newTab.cards) {
      cards = newTab.cards.map((card) => {
        const extraInfo = this.buildExtraInfoModal(card);
        return {
          title: card.titulo,
          description: card.descricao,
          extraInfo,
        };
      });
    }
    return cards;
  }

  private buildExtraInfoModal(card: any) {
    let extraInfo = [];
    if (card.extraInfo && card.extraInfo.length > 0) {
      extraInfo = card.extraInfo.map((info) => {
        const newInfo = info.flatData;
        return {
          title: newInfo.titulo,
          description: newInfo.descricao,
          category: newInfo.categoria,
        };
      });
    }
    return extraInfo;
  }

  private mapPageInc(page: any) {
    return {
      titulo: page.titulo ? page.titulo : '',
      subtitulo: page.subtitulo ? page.subtitulo : '',
      urlIframe: page.urlIframe ? page.urlIframe : null,
    };
  }

  private mapDynamicTable(page: any) {
    let content = '';
    if (page.header && page.header !== null && page.header !== '') {
      content = page.header
        .replace(/tbody/g, 'thead')
        .replace('</table>', '')
        .replace(/td/g, 'td style="text-align: center"');
    } else {
      content = '<table>';
    }

    if (page.data && page.data.length > 0) {
      content = content + '<tbody>';
      page.data.map((item) => {
        const data = JSON.parse(item.flatData.content);
        data.map((row) => {
          content = content + '<tr>';
          Object.keys(row).map((key) => {
            const value = row[key];
            // if (!isNaN(+this.clearForNumber(value))) {
            //   content = content + '<td style="text-align: right">' + value + '</td>';
            // } else {
            content =
              content + '<td style="text-align: center">' + value + '</td>';
            // }
          });
          content = content + '</tr>';
        });
      });
      content = content + '</tbody>';
    }
    content = content + '</table>';

    return {
      textAfter: page.textAfter,
      textBefore: page.textBefore,
      content,
    };
  }

  private clearForNumber(value) {
    let str = value;
    if (str) {
      str = value.replace(/\./g, '');
      str = str.replace(/\,/g, '');
      str = str.replace(/\%/g, '');
      str = str.replace(/\R$/g, '');
    }
    return str;
  }

  private buildFeatureItensArray(features: any) {
    let data = [];
    let buttons = [];
    if (features.itens) {
      data = features.itens.iv.map((item) => {
        return {
          title: item.titulo || '',
          subtitle: item.subtitulo || '',
          description: item.descricao || '',
          externalLink: item.linkExterno || null,
          img:
            item.imagem && item.imagem[0]
              ? this.assetsURL + item.imagem[0].id
              : '',
          modal: item.modal ? item.modal : null,
        };
      });
    }

    if (features.botoes && features.botoes.iv.length > 0) {
      buttons = features.botoes.iv.map((item) => {
        return {
          title: item.titulo || '',
          color: item.cor || '',
          target: item.target && item.target[0] ? item.target[0].id : '',
        };
      });
    }

    return {
      title: features.titulo ? features.titulo.iv : '',
      subtitle: features.subtitulo ? features.subtitulo.iv : '',
      data,
      buttons,
    };
  }

  private buildResponseCounterObject(response: any) {
    let counters = [];
    let buttons = [];

    if (response.contadores) {
      counters = response.contadores.iv.map((counter) => {
        return {
          limit: counter.valor,
          scale: counter.scale,
          icon:
            counter.imagem && counter.imagem[0]
              ? this.assetsURL + counter.imagem[0].id
              : '',
          description: counter.descricao,
        };
      });
    }

    if (response.botoes) {
      buttons = response.botoes.iv.map((button) => {
        let externalURL = false;
        if (button.uRL) {
          if (button.uRL.indexOf('http') >= 0) {
            externalURL = true;
          }
        }
        return {
          modal: button.modal && button.modal[0] ? button.modal[0].id : '',
          color: button.cores,
          title: button.titulo,
          url: button.url,
          router: externalURL ? null : button.uRL ? button.uRL : null,
          hrefTarget: externalURL ? '_blank' : null,
        };
      });
    }

    return {
      title: response.titulo ? response.titulo.iv : response.titulo,
      subtitle: response.subtitulo ? response.subtitulo.iv : response.subtitulo,
      img:
        response.imagem && response.imagem.iv && response.imagem.iv[0]
          ? this.assetsURL + response.imagem.iv[0].id
          : '',
      counters,
      buttons,
    };
  }

  private buildReponseFormularioObject(form: any) {
    return {
      description: form.titulo.iv,
      email: form.email.iv,
      subject: form.assuntoMensagem ? form.assuntoMensagem.iv : null,
      body: form.corpoMensagem
        ? encodeURIComponent(form.corpoMensagem.iv)
        : null,
      isPrimary: form.corDeFundo ? form.corDeFundo.iv : form.corDeFundo,
    };
  }

  private buildReponseChartsObject(response: any) {
    const chart = {
      barChartLabels: [],
      barChartType: response.tipo.iv === 'Vertical' ? 'bar' : 'horizontalBar',
      barChartColors: [],
      barChartLegend: false,
      barChartPlugins: [pluginDataLabels],
      barChartData: [],
      barChartOptions: {
        responsive: true,
        tooltips: { enabled: false },
        legend: { display: false },
        scales: {
          xAxes: [
            {
              display: response.tipo.iv === 'Vertical',
              gridLines: { display: true },
            },
          ],
          yAxes: [
            {
              display: response.tipo.iv !== 'Vertical',
              gridLines: { display: true },
            },
          ],
        },
        layout: {
          padding: {
            left: 0,
            right: 30,
            top: 20,
            bottom: 0,
          },
        },
        plugins: { datalabels: { anchor: 'end', align: 'end' } },
      },
    };

    if (response.eixo) {
      const barChartColors = { backgroundColor: [] };
      const barChartData = {
        maxBarThickness: 40,
        data: [],
      };
      response.eixo.iv.forEach((eixo) => {
        barChartColors.backgroundColor.push(eixo.cor);
        barChartData.data.push(eixo.valor);
        chart.barChartLabels.push([eixo.rotulo]);
      });

      chart.barChartColors.push(barChartColors);
      chart.barChartData.push(barChartData);
    }

    return {
      title: response.titulo ? response.titulo.iv : '',
      subtitle: response.subtitulo ? response.subtitulo.iv : '',
      description: response.descricao ? response.descricao.iv : '',
      footer: response.rodape ? response.rodape.iv : '',
      chart,
    };
  }

  private buildResponseHighlightAnchorObject(highlightAnchor: any) {
    let buttons = [];
    if (highlightAnchor.botoes) {
      buttons = highlightAnchor.botoes.map((button) => {
        let externalURL = false;
        if (button.uRL) {
          if (button.uRL.indexOf('http') >= 0) {
            externalURL = true;
          }
        }

        return {
          color: button.cor,
          title: button.titulo,
          url: button.uRL,
          modal: button.modal && button.modal[0] ? button.modal[0].id : '',
          router: externalURL ? null : button.uRL ? button.uRL : null,
          hrefTarget: externalURL ? '_blank' : null,
        };
      });
    }

    return {
      buttons,
      link: highlightAnchor.navegarPara
        ? highlightAnchor.navegarPara === 'Próxima seção'
          ? 'next'
          : 'previous'
        : highlightAnchor.navegarPara,
      description: highlightAnchor.descricao
        ? highlightAnchor.descricao
        : highlightAnchor.descricao,
      filter: highlightAnchor.filter === 'Escuro' ? 'dark' : 'colors',
      image:
        highlightAnchor.imagem &&
        highlightAnchor.imagem &&
        highlightAnchor.imagem[0]
          ? this.assetsURL + highlightAnchor.imagem[0].id
          : '',
    };
  }

  private buildResponseCardInfoObject(card: any) {
    let buttons = [];
    if (card.botoes) {
      buttons = card.botoes.map((button) => {
        return {
          color: button.cor,
          title: button.titulo,
          url: button.uRL,
          modal: button.modal.length > 0 ? button.modal[0].id : null,
        };
      });
    }

    return {
      buttons,
      description: card.descricao ? card.descricao : null,
      highlightText: card.textoDestaque ? card.textoDestaque : null,
      mainInfo: card.informacaoPrincipal ? card.informacaoPrincipal : null,
    };
  }

  private buildResponseCardIconeTituloLinkObject(card: any) {
    return {
      highlightText: card.textoDestaque ? card.textoDestaque : null,
      icon: card.icone && card.icone[0]
        ? this.assetsURL + card.icone[0].id
        : null,
      externalLink: card.uRL
        ? card.uRL
        : null,
      page: card.pagina && card.pagina.length > 0
          ? '/paginas/' + card.pagina[0].data.slug.iv
          : null,
      hasTxtUrl: card.mostraLink
    };
  }

  private buildResponseIconeTituloObject(response: any) {
    return {
      icon: response.icone && response.icone[0]
        ? this.assetsURL + response.icone[0].id
        : null,
      highlightText: response.titulo ? response.titulo : null
    };
  }

  private buildResponseDescricaoCorFundoObject(response: any) {
    return {
      description: response.descricao,
      txtColor: response.corTexto ? response.corTexto : null,
      bgColor: response.corFundo ? response.corFundo : null,
      centerTxt: response.centralizar,
      hasMarginBottom: response.hasMarginBottom
    };
  }

  private buildResponseImagemTituloDescricaoObject(response: any) {
    return {
      image: response.imagem && response.imagem[0]
        ? this.assetsURL + response.imagem[0].id
        : null,
      title: response.titulo ? response.titulo : null,
      description: response.descricao ? response.descricao : null,
    };
  }

  private buildResponseImagemObject(response: any) {
    return {
      image: response.imagem && response.imagem[0]
        ? this.assetsURL + response.imagem[0].id
        : null,
    };
  }

  private buildResponseLinkInternoVoltarObject(response: any) {
    return {
      title: response.titulo ? response.titulo : null,
      page: response.pagina && response.pagina.length > 0
        ? '/paginas/' + response.pagina[0].data.slug.iv
        : null,
    };
  }

  private buildResponseTituloSubtituloCorFundoObject(response: any) {
    return {
      title: response.titulo ? response.titulo : null,
      subtitle: response.subtitulo ? response.subtitulo : null,
      bgColor: response.corFundo ? response.corFundo : null,
    };
  }

  private buildResponseBannerTextoGrandeObject(response: any) {
    return {
      title: response.titulo,
      subtitle: response.subtitulo,
      image: response.imagem && response.imagem[0]
        ? this.assetsURL + response.imagem[0].id
        : null,
    };
  }

  private buildReponseHighlightHealthObject(highlightHealth: any) {
    let cards: Array<CardData> = [];

    if (highlightHealth.cards) {
      cards = highlightHealth.cards.iv.map(
        (card): CardData => {
          return {
            image:
              card.imagem && card.imagem[0]
                ? this.assetsURL + card.imagem[0].id
                : '',
            modal: card.modal,
            subtitle: card.subtitulo,
            title: card.titulo,
            imageHover: card.imageHover,
            hasHover: card.hasHover,
          };
        }
      );
    }

    return {
      title: highlightHealth.titulo ? highlightHealth.titulo.iv : '',
      subtitle: highlightHealth.subtitulo ? highlightHealth.subtitulo.iv : '',
      cards,
    } as SectionCardSaudeData;
  }

  public buildReponseSocialObject(response: any) {
    let cards = [];

    if (response?.cards) {
      cards = response.cards.iv.map((card: any) => {
        return {
          title: card.titulo,
          description: card.descricao,
          image:
            card.imagem && card.imagem[0]
              ? this.assetsURL + card.imagem[0].id
              : '',
        };
      });
    }
    return {
      cards,
      title: response?.titulo ? response.titulo.iv : '',
    };
  }

  private mapBanner(banner) {
    const hasButton = banner.botoes && banner.botoes.length > 0;
    let buttons = [];
    if (hasButton) {
      buttons = banner.botoes.map((button) => {
        let externalURL = false;
        if (button.uRL) {
          if (button.uRL.indexOf('http') >= 0) {
            externalURL = true;
          }
        }
        return {
          title: button.titulo ? button.titulo : '',
          color: button.cor ? button.cor : '',
          url: button.uRL ? button.uRL : '',
          modal: button.modal && button.modal[0] ? button.modal[0].id : null,
          target: button.target && button.target[0] ? button.target[0].id : '',
          router: externalURL ? null : button.uRL ? button.uRL : null,
          hrefTarget: externalURL ? '_blank' : null,
          page: button.page.length
            ? '/paginas/' + button.page[0].data.slug.iv
            : '',
        };
      });
    }
    const hasMenu = !!banner.menu && banner.menu.length > 0;
    let menu = [];
    if (hasMenu) {
      menu = banner.menu.map((menuItem) => {
        return {
          title: menuItem.titulo ? menuItem.titulo : '',
          url: menuItem.uRL ? menuItem.uRL : '',
          modal:
            menuItem.modal && menuItem.modal[0] ? menuItem.modal[0].id : null,
          page: menuItem.pagina
            ? menuItem.pagina.length > 0
              ? '/paginas/' + menuItem.pagina[0].flatData.slug
              : null
            : null,
        };
      });
    }
    const hasLinks = !!banner.links && banner.links.length > 0;
    let links = [];
    if (hasLinks) {
      links = banner.links.map((link) => {
        return {
          title: link.titulo,
          icon: link.imagem[0] ? this.assetsURL + link.imagem[0].id : null,
          url: link.pagina[0]
            ? '/paginas/' + link.pagina[0].flatData.slug
            : link.uRL
            ? '/' + link.uRL
            : null,
          modal: link.modal[0] ? link.modal[0] : null,
        };
      });
    }
    let profiles;
    const hasProfiles = !!banner.profiles;
    if (hasProfiles) {
      profiles = this.getProfiles().toPromise();
    }
    return {
      title: banner.titulo ? banner.titulo : null,
      description: banner.descricao ? banner.descricao : null,
      image:
        banner.imagem && banner.imagem[0]
          ? this.assetsURL + banner.imagem[0].id
          : '',
      center: buttons.length > 1 ? true : false,
      hasBottomNav: banner.redeCredenciada ? banner.redeCredenciada : false,
      hasMargin: banner.hasMargin ? banner.hasMargin : false,
      hasProfiles,
      profiles,
      buttons,
      links,
      menu,
    } as BannerData;
  }

  private mapInsurance(response, name) {
    const data = response.itens.iv
      .filter((item) => {
        let pagina = item.pagina[0]
          ? item.pagina[0].data.slug.iv.split('/')
          : null;
        if (pagina.length > 0) {
          pagina = pagina[1];
        }
        return pagina !== name && pagina !== 'acidente-pessoal';
      })
      .map((item) => {
        return {
          rows: item.linhas,
          cols: item.colunas,
          img: item.imagemFundo
            ? this.assetsURL + item.imagemFundo[0].id
            : null,
          icon:
            item.icone && item.icone[0]
              ? this.assetsURL + item.icone[0].id
              : null,
          title: item.titulo.replace('Seguro ', '').replace('de ', ''),
          subtitle: item.subtitulo,
          descriptionActionButton: item.botaoDescricao
            ? item.botaoDescricao
            : null,
          modal: item.modal && item.modal[0] ? item.modal : null,
          page:
            item.pagina && item.pagina[0]
              ? '/paginas/' + item.pagina[0].data.slug.iv
              : null,
        };
      });
    return data;
  }

  private mapBenefits(response) {
    let links;
    if (response.links) {
      links = response.links.iv.map((link) => {
        return {
          title: link.titulo,
          url: link.uRL,
          modal: link.modal,
        };
      });
    }
    const data = response.itens.iv.map((item) => {
      const pageRoute =
        item.pagina && item.pagina[0]
          ? '/paginas/' + item.pagina[0].data.slug.iv
          : null;
      return {
        rows: item.linhas,
        cols: item.colunas,
        img: item.imagemFundo ? this.assetsURL + item.imagemFundo[0].id : null,
        icon:
          item.icone && item.icone[0]
            ? this.assetsURL + item.icone[0].id
            : null,
        title: item.titulo,
        subtitle: item.subtitulo,
        descriptionActionButton: item.botaoDescricao
          ? item.botaoDescricao
          : null,
        modal: item.modal && item.modal[0] ? item.modal : null,
        page: pageRoute,
        externalLink: item.externalLink ? item.externalLink : null,
        document:
          item.pdf && item.pdf[0] ? this.assetsURL + item.pdf[0].id : null,
        buttons: item.botaoDescricao
          ? [
              {
                title: item.botaoDescricao,
                url: null,
                page: null,
                color: 'Texto Primário',
              },
            ]
          : null,
      };
    });
    return {
      title: response.titulo.iv,
      subtitle: response.subtitulo.iv,
      links,
      data,
    };
  }

  private mapBenefitsFlat(response) {
    let links;
    if (response.links) {
      links = response.links.map((link) => {
        return {
          title: link.titulo,
          url: link.uRL,
          modal: link.modal,
        };
      });
    }
    const data = response.itens.map((item) => {
      const pageRoute =
        item.pagina && item.pagina[0]
          ? '/paginas/' + item.pagina[0].flatData.slug
          : null;
      return {
        rows: item.linhas,
        cols: item.colunas,
        img: item.imagemFundo ? this.assetsURL + item.imagemFundo[0].id : null,
        icon:
          item.icone && item.icone[0]
            ? this.assetsURL + item.icone[0].id
            : null,
        title: item.titulo,
        subtitle: item.subtitulo,
        descriptionActionButton: item.botaoDescricao
          ? item.botaoDescricao
          : null,
        modal: item.modal && item.modal[0] ? item.modal : null,
        page: pageRoute,
        externalLink: item.externalLink ? item.externalLink : null,
        document:
          item.pdf && item.pdf[0] ? this.assetsURL + item.pdf[0].id : null,
        buttons: item.botaoDescricao
          ? [
              {
                title: item.botaoDescricao,
                url: null,
                page: null,
                color: 'Texto Primário',
              },
            ]
          : null,
      };
    });
    return {
      title: response.titulo,
      subtitle: response.subtitulo,
      links,
      data,
    };
  }

  private mapSection(section) {
    const links = section.links.iv.map((link) => {
      return {
        link: link.link,
        externalLink: link.linkExterno,
        description: link.descricaoLink,
        title: link.titulo,
        image: link.imagem ? this.assetsURL + link.imagem[0].id : null,
        modal: link.modal ? link.modal : null,
        document:
          link.documento && link.documento.length > 0
            ? this.assetsURL + link.documento[0].id
            : null,
      };
    });
    const hasFooter = section.rodape !== null && section.rodape !== undefined;
    return {
      links,
      footer: hasFooter ? section.rodape.iv : section.rodape,
      description: section.subtitulo.iv,
      title: section.titulo.iv,
    };
  }

  private mapServices(response) {
    let buttons = [];
    if (response.botoes) {
      buttons = response.botoes.iv.map((button) => {
        let externalURL = false;
        if (button.uRL) {
          if (button.uRL.indexOf('http') >= 0) {
            externalURL = true;
          }
        }
        return {
          color: button.cor,
          title: button.titulo,
          url: button.uRL,
          router: externalURL ? null : button.uRL ? button.uRL : null,
          hrefTarget: externalURL ? '_blank' : null,
          modal: button.modal && button.modal[0] ? button.modal[0].id : '',
          target: button.target && button.target[0] ? button.target[0].id : '',
          page:
            button.pagina && button.pagina.length > 0
              ? '/paginas/' + button.pagina[0].data.slug.iv
              : '',
        };
      });
    }

    let icons = [];
    if (response.icones) {
      icons = response.icones.iv.map((icon) => {
        return {
          title: icon.titulo,
          image: icon.imagem ? this.assetsURL + icon.imagem[0].id : null,
          url: icon.uRL || '',
          modal: icon.modal && icon.modal[0] ? icon.modal[0].id : '',
          page:
            icon.pagina && icon.pagina.length > 0
              ? '/paginas/' + icon.pagina[0].data.slug.iv
              : '',
        };
      });
    }
    let tips;
    if (response.dicas !== null && response.dicas !== undefined) {
      tips = response.dicas.iv.map((tip) => {
        return {
          description: tip.descricao,
        };
      });
    } else {
      tips = response.dicas;
    }
    const hasHighlightColor =
      response.corDestaque !== null && response.corDestaque !== undefined;
    const hasDescription =
      response.descricao !== null && response.descricao !== undefined;

    let links = [];
    if (response.links) {
      links = response.links.iv.map((link) => {
        return {
          title: link.title || '',
          page: link.page[0] ? '/paginas/' + link.page[0].data.slug.iv : '',
          modal: link.modal[0] ? link.modal[0].id : '',
        };
      });
    }

    return {
      buttons,
      highlightColor: hasHighlightColor
        ? response.corDestaque.iv
        : response.corDestaque,
      tips,
      icons,
      image: response.imagem ? this.assetsURL + response.imagem.iv[0].id : null,
      subtitle: response.subtitulo ? response.subtitulo.iv : '',
      description: hasDescription ? response.descricao.iv : '',
      title: response.titulo ? response.titulo.iv : '',
      imgFirst: response.imagemPrimeiro ? response.imagemPrimeiro.iv : null,
      marginBottom: response.espacoAbaixo ? response.espacoAbaixo.iv : null,
      links,
    };
  }

  private mapFaq(response) {
    const content = response.conteudo.map((data) => {
      let documents = [];
      if (data.documentos && data.documentos.length > 0) {
        documents = data.documentos[0].flatData.documents.map((item) => {
          return {
            title: item.title,
            link: item.file[0].url,
          };
        });
      }
      let components = [];
      if (data.components && data.components.length > 0) {
        components = data.components;
      }
      return {
        title: data.titulo,
        description: data.descricao,
        documents,
        components,
      };
    });

    return {
      title: response.title,
      content,
    };
  }

  private mapFilterResponse(response) {
    const hasItems = response.items.length > 0;
    const isObject = response.items.length === 1;
    const isArray = response.items.length > 1;
    if (hasItems) {
      if (isArray) {
        return response.items.map((item) => {
          return this.mapServices(item.data);
        });
      }
      if (isObject) {
        return this.mapServices(response.items[0].data);
      }
    }
    return response.items;
  }

  private mapNavActions(response) {
    return response.map((item) => {
      return {
        title: item.data && item.data.titulo ? item.data.titulo.iv : '',
        color: item.data && item.data.botao ? item.data.botao.iv : '',
        pages:
          item.data && item.data.pages && item.data.pages.iv[0]
            ? item.data.pages.iv.map(this.mapPageNavActions)
            : [],
      };
    });
  }

  private mapPageNavActions(item) {
    return {
      title: item.title || '',
      externalLink: item.externalLink || '',
      page:
        item.page &&
        item.page[0] &&
        item.page[0].data &&
        item.page[0].data.slug &&
        item.page[0].data.slug.iv
          ? '/paginas/' + item.page[0].data.slug.iv
          : '',
    };
  }

  private mapMenuHamburger(response) {
    return response
      .map((menu) => {
        const m = menu.data;
        return {
          icon: m.icon && m.icon.iv[0] ? this.assetsURL + m.icon.iv[0].id : '',
          title: m.title ? m.title.iv : '',
          firstSection:
            m.firstSection && m.firstSection.iv[0]
              ? m.firstSection.iv.map(this.mapSectionMenuHamburger)
              : m.firstSection.iv,
          subtitle: m.subtitle ? m.subtitle.iv : '',
          secondSection:
            m.secondSection && m.secondSection.iv[0]
              ? m.secondSection.iv.map(this.mapSectionMenuHamburger)
              : m.secondSection.iv,
          position: m.position ? m.position.iv : 0,
        };
      })
      .sort((a, b) => a.position - b.position);
  }

  private mapSectionMenuHamburger(item) {
    item.description = item.description || '';
    item.page = item.page?.length ? '/paginas/' + item.page[0].data.slug.iv : '';
    if (item.link) item.link = item.link || '';
    if (item.modal) item.modal = item.modal[0] ? item.modal[0].id : '';

    return item;
  }

  private mapClinicas(response) {
    let clinicas = [];
    if (response.clinicas) {
      clinicas = response.clinicas.map((clinica) => {
        return {
          name: clinica.nomeClinica,
          address: clinica.endereco,
          hours: clinica.horarios
        };
      });
    }
    return {
      title: response.titulo,
      clinicas
    };
  }

  public openModal(id: string, preview?: boolean) {
    const _id = id.split(',');
    this.getModal(_id[0], preview).subscribe((response) => {
      if (response[0]) {
        response[0].idTab = _id[1] || '';
        this.modalService.open(response[0]);
        this.overlay.close.emit();
      }
    });
  }

  public getFilterPension() {
    return this.return('filtro-previdencia').pipe(
      map((value) => {
        return value.items.length > 0 ? value.items[0].data : value.items;
      }),
      map((response) => {
        return response.Passos.iv.map((passo) => {
          return {
            label: passo['Legenda'],
            title: passo['Titulo'],
            subtitle: passo['Subtitulo'],
          } as Step;
        });
      })
    );
  }

  public getPensionPlans(term: string): Observable<any> {
    const schema = 'graphql';
    const params = {
      query:
        'query{queryPlanosPrevidenciaContents(orderby: "data/Titulo/iv"){data{titulo{iv}planos{iv{titulo subtitulo destino{data{slug{iv}}}}}}}}',
    };

    const url =
      environment.configCMS.endPointAPI +
      environment.configCMS.endPointAPP +
      schema;
    const token = this.cmsSessionService.getToken();

    return this.post(url, params, '', token).pipe(
      map((value: HttpResponse<any>) => {
        return value.body.data.queryPlanosPrevidenciaContents.map(
          this.buildFamilyObject
        );
      }),
      map((terms) => {
        if (term) {
          return terms.filter((termItem) =>
            termItem.title.toLowerCase().includes(term.toLowerCase())
          );
        } else {
          return terms;
        }
      })
    );
  }

  buildFamilyObject(value: PlanDataResponse): any {
    const hasPlans =
      value.data.planos !== null && value.data.planos !== undefined;
    let childrens = [];
    if (hasPlans) {
      childrens = value.data.planos.iv.map((plan) => {
        return {
          title: plan.titulo,
          subtitle: plan.subtitulo,
          link:
            plan.destino && plan.destino.length > 0
              ? '/paginas/' + plan.destino[0].data.slug.iv
              : '',
        };
      });
    }
    return {
      title: value.data.titulo.iv,
      childrens,
    };
  }

  getFilterHealth() {
    return this.return('filtro-saude').pipe(
      map((value) => {
        return value.items.length > 0 ? value.items[0].data : value.items;
      }),
      map((response) => {
        return response.Passos.iv.map((passo) => {
          return {
            label: passo['Legenda'],
            title: passo['Titulo'],
            subtitle: passo['Subtitulo'],
          } as Step;
        });
      })
    );
  }

  getHealthPlans(term: string) {
    const schema = 'graphql';
    const params = {
      query: HealthPlansQuery(),
    };

    const url =
      environment.configCMS.endPointAPI +
      environment.configCMS.endPointAPP +
      schema;
    const token = this.cmsSessionService.getToken();

    return this.post(url, params, '', token).pipe(
      map((value: HttpResponse<any>) => {
        return value.body.data.queryFamiliaSaudeContents.map((family) => {
          return this.buildFamilyHealthObject(family);
        });
      }),
      map((terms) => {
        if (term) {
          return terms.filter((termItem) =>
            termItem.title.toLowerCase().includes(term.toLowerCase())
          );
        } else {
          return terms;
        }
      })
    );
  }

  getHealthFamily() {
    const schema = 'graphql';
    const params = {
      query: HealthPlansQuery(),
    };

    const url =
      environment.configCMS.endPointAPI +
      environment.configCMS.endPointAPP +
      schema;
    const token = this.cmsSessionService.getToken();

    return this.post(url, params, '', token).pipe(
      map((value: HttpResponse<any>) => {
        return value.body.data.queryFamiliaSaudeContents.map((family) => {
          return this.buildFamilyHealthObject(family);
        });
      })
    );
  }

  public buildFamilyHealthObject(objFamily) {
    const id = objFamily.id;
    const title = objFamily.data.titulo.iv;
    const icon = this.assetsURL + objFamily.data.icone.iv[0].id;
    const childrens = objFamily.data.empresas.iv.map((item) => {
      let plans = [];
      const enterprise = item.data.titulo.iv;
      if (item.data.planos.iv.length > 0) {
        plans = item.data.planos.iv.map((plan) => {
          return {
            title: plan.plano[0].data.titulo.iv,
            link: plan.pagina[0]
              ? 'paginas/' + plan.pagina[0].data.slug.iv
              : null,
          };
        });
      } else {
        plans = [
          {
            title: item.data.planos.iv.plano[0].data.titulo.iv,
            link: item.data.planos.iv.pagina
              ? 'paginas/' + item.data.planos.iv.pagina[0].data.slug.iv
              : null,
          },
        ];
      }
      return {
        title: enterprise,
        childrens: plans,
      };
    });

    return {
      id,
      title,
      icon,
      childrens,
    };
  }

  getHelpdesk() {
    const schema = 'graphql';
    const params = {
      query: HelpdeskQuery(),
    };

    const url =
      environment.configCMS.endPointAPI +
      environment.configCMS.endPointAPP +
      schema;
    const token = this.cmsSessionService.getToken();

    return this.post(url, params, '', token).pipe(
      map((value: HttpResponse<any>) => {
        return value.body.data.queryAtendimentoContents.map((family) => {
          return this.buildHelpdeskObject(family);
        });
      })
    );
  }

  public getResultsSearchs(term): Observable<any> {
    const requests = [
      this.send('graphql', { query: searchPageByTags(term) }),
      this.send('graphql', { query: searchNewsQuery(term) }),
      this.send('graphql', { query: searchPublicationsQuery(term) }),
    ];

    return forkJoin(requests);
  }

  public getPublishingAbout(param: string): Observable<any> {
    const params = {
      query: publishingAboutQuery(param),
    };

    return this.send('graphql', params).pipe(
      map((response) => {
        const items = response.body.data.queryDetalhesPublicacoesContents;
        return this.mapPublishingAbout(items);
      }),
      catchError((error) => {
        if (!environment.production) {
          console.log(error);
        }
        return of(null);
      })
    );
  }

  private mapPublishingAbout(obj) {
    return obj.map((item) => {
      const data = item.data;
      return {
        category: data.category.iv,
        title: data.titulo.iv,
        subtitle: data.subtitulo.iv,
        banner: data.imagemDestaque.iv[0].url,
        date: data.data.iv,
        modal: data.modal.iv[0].id,
        description: null,
      };
    });
  }

  public getSurvey(slug: string): Observable<any> {
    const params = {
      query: SurveyQuery(slug),
    };

    return this.send('graphql', params).pipe(
      map((response) => {
        const items = response.body.data.queryQuestionarioContents[0].flatData;
        return this.mapSurvey(items);
      }),
      catchError((error) => {
        if (!environment.production) {
          console.log(error);
        }
        return of(null);
      })
    );
  }

  private mapSurvey(response) {
    const questions = response.questions.map((item, index) => {
      return {
        id: index,
        slug: item.slug,
        title: item.question,
        icon: item.icon.length > 0 ? this.assetsURL + item.icon[0].id : null,
        type: item.type,
        rating: 0,
      };
    });
    return {
      slug: response.slug,
      title: response.name,
      minMinutes: response.minMinutes,
      minPages: response.minPages,
      questions,
    };
  }

  public async postSurvey(survey) {
    const params = await this.mapPostSurvey(survey);
    this.sendSurvey(params).subscribe((response) => {
      setTimeout(() => {
        this.cookieService.set('survey', survey.slug);
      }, 3000);
    });
  }

  private async mapPostSurvey(response) {
    const now = new Date().toISOString().split('.')[0] + 'Z';
    const ip = await this.IP.getIP();

    const questions = response.questions.map((item, index) => {
      return {
        SlugPergunta: item.slug,
        Resposta: '' + item.rating,
      };
    });
    return {
      SlugQuestionario: { iv: response.slug },
      DataResposta: { iv: now },
      IPResposta: { iv: ip },
      Respostas: { iv: questions },
    };
  }

  private sendSurvey(params) {
    return this.send('questionario-respostas/?publish=true', params).pipe(
      map((response) => {
        console.log('send', response);
      }),
      catchError((error) => {
        if (!environment.production) {
          console.log(error);
        }
        return of(null);
      })
    );
  }

  public getFloating(): Observable<any> {
    const params = {
      query: FooterQuery(),
    };

    return this.send('graphql', params).pipe(
      map((response) => {
        const items =
          response.body.data.queryRodapeContents[0].flatData.flutuante;
        return this.mapFloatingGraphQL(items);
      }),
      catchError((error) => {
        if (!environment.production) {
          console.log(error);
        }
        return of(null);
      })
    );
  }

  private mapFloatingGraphQL(components) {
    const hasItems = components.length > 0;
    const isObject = components.length === 1;
    const isArray = components.length > 1;

    if (hasItems) {
      if (isArray) {
        return components.map((component) => {
          return this.buildFloating(component);
        });
      }

      if (isObject) {
        return [this.buildFloating(components[0])];
      }
    }
  }

  private buildFloating(component) {
    const newComponent = component;
    switch (newComponent.__typename) {
      case 'Atendimento':
        newComponent.data = this.buildHelpdeskObject(component.flatData);
        break;
      case 'Questionario':
        newComponent.data = this.mapSurvey(component.flatData);
        break;
      default:
        break;
    }
    return newComponent;
  }

  public buildHelpdeskObject(obj) {
    let buttons = [];
    if (obj.botoes) {
      buttons = obj.botoes.map((item) => {
        return {
          title: item.titulo,
          url: item.uRL,
        };
      });
    }
    return {
      title: obj.titulo,
      titleMini: obj.tituloMini,
      description: obj.descricao,
      icon: obj.icone ? this.assetsURL + obj.icone[0].id : null,
      buttons,
    };
  }

  buildResponseNewsCarousel(data) {
    data.news = this.getHighlightNews(data.max).toPromise();
    return data;
  }

  public getConfig(): Observable<any> {
    const params = {
      query: ConfigQuery(),
    };
    return this.send('graphql', params).pipe(
      map((response) => {
        const config =
          response.body.data.queryConfiguracaoSiteContents[0].flatData;
        return this.mapConfig(config);
      }),
      catchError((error) => {
        if (!environment.production) {
          console.log(error);
        }
        this.router.navigate(['/nao-encontrado']);
        return of(null);
      })
    );
  }

  mapConfig(config) {
    return {
      mainPage: '/paginas/' + config.mainPage[0].flatData.slug,
    };
  }

}
